import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, FileText, ArrowRight } from "lucide-react";
import DnaHelix from "@/components/DnaHelix";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";
import { toast } from "@/components/ui/sonner";

export default function ProductCard({ material }) {
    const { user, openAuth } = useAuth();
    const { add, has } = useCart();
    const { isFavorite, toggle } = useFavorites();
    const variants = material.variants || [];
    const [selected, setSelected] = useState(0);
    const variant = variants[selected] || null;
    const fav = isFavorite(material.id);
    const inCart = has(material.id, variant);
    // variant-specific product photo (strength band baked into the label); v0 = canonical
    const imgSrc =
        selected > 0 && material.image_url
            ? material.image_url.replace(/\.jpg$/, `-v${selected}.jpg`)
            : material.image_url || "/assets/vial-product.jpg";

    const price = variant ? variant.price : null;
    const availability = !variants.length
        ? { label: "Availability pending", tone: "pending" }
        : variant?.available
          ? { label: "In Stock", tone: "in" }
          : { label: "Unavailable", tone: "out" };

    const handleFavorite = async () => {
        if (!user) {
            openAuth("register");
            return;
        }
        try {
            const nowFav = await toggle(material.id);
            toast.success(nowFav ? "Saved to your materials" : "Removed from saved materials", {
                description: material.name,
            });
        } catch {
            toast.error("Could not update saved materials");
        }
    };

    const handleAdd = () => {
        if (!variants.length) return;
        if (inCart) return;
        add(material, variant);
        toast.success("Added to cart", {
            description: `${material.name}${variant?.label ? ` — ${variant.label}` : ""}`,
        });
    };

    return (
        <article
            data-testid={`material-card-${material.id}`}
            className="card-lift h-full flex flex-col bg-[#FDFAF5] border border-taupe/50 hover:border-wine/40 hover:shadow-[0_16px_50px_rgba(31,42,58,0.10)] overflow-hidden"
        >
            {/* Product image — clickable, opens product details */}
            <Link
                to={`/materials/${material.id}`}
                data-testid={`material-image-link-${material.id}`}
                aria-label={`View details for ${material.name}`}
                className="relative block border-b border-stone-200 bg-surface2 group"
            >
                <img
                    src={imgSrc}
                    alt={material.name}
                    onError={(e) => {
                        if (!e.currentTarget.dataset.fbk && material.image_url && e.currentTarget.src !== material.image_url) {
                            e.currentTarget.dataset.fbk = "1";
                            e.currentTarget.src = material.image_url;
                        }
                    }}
                    className="w-full aspect-[4/5] object-cover object-center transition-transform duration-500 group-hover:scale-[1.03]"
                    data-testid={`material-image-${material.id}`}
                />
                <DnaHelix className="absolute bottom-2 right-2 h-16 w-auto text-white/70 opacity-0 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none" />
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFavorite();
                    }}
                    aria-label={fav ? "Remove from saved materials" : "Save material"}
                    data-testid={`material-fav-${material.id}`}
                    className={`absolute top-3 right-3 h-8 w-8 rounded-full bg-white/90 border flex items-center justify-center transition-colors duration-300 ${
                        fav ? "border-gold text-gold" : "border-stone-200 text-slate-400 hover:text-wine hover:border-gold/60"
                    }`}
                >
                    <Heart className={`h-4 w-4 ${fav ? "fill-current" : ""}`} strokeWidth={1.5} />
                </button>
                {material.is_placeholder && (
                    <span
                        data-testid={`material-placeholder-tag-${material.id}`}
                        className="absolute top-3 left-3 text-[0.55rem] uppercase tracking-[0.2em] font-semibold text-slate-400 border border-dashed border-stone-300 bg-white/85 px-2.5 py-1"
                    >
                        Placeholder
                    </span>
                )}
            </Link>

            <div className="flex flex-col flex-1 p-4 sm:p-6">
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                    <p className="text-[0.6rem] uppercase tracking-[0.22em] font-semibold text-gold">{material.category}</p>
                    <span
                        data-testid={`material-availability-${material.id}`}
                        className={`inline-flex items-center gap-1.5 text-[0.55rem] uppercase tracking-[0.15em] font-semibold ${
                            availability.tone === "in"
                                ? "text-emerald-700"
                                : availability.tone === "out"
                                  ? "text-red-500"
                                  : "text-slate-400"
                        }`}
                    >
                        <span
                            className={`h-1.5 w-1.5 rounded-full ${
                                availability.tone === "in"
                                    ? "bg-emerald-500"
                                    : availability.tone === "out"
                                      ? "bg-red-400"
                                      : "bg-slate-300"
                            }`}
                        />
                        {availability.label}
                    </span>
                </div>

                <h3 className="mt-2 font-serif text-lg sm:text-xl font-light text-brand leading-snug">{material.name}</h3>
                {material.unit && <p className="mt-1 text-[0.65rem] text-slate-500">{material.unit}</p>}
                {material.description && (
                    <p className="mt-3 text-xs leading-relaxed text-mist line-clamp-3 flex-none">{material.description}</p>
                )}

                {/* Variant selector */}
                {variants.length > 0 && (
                    <div className="mt-4" data-testid={`material-variants-${material.id}`}>
                        <p className="text-[0.6rem] uppercase tracking-[0.2em] text-slate-400 mb-2">Size / Strength</p>
                        <div className="flex flex-wrap gap-2">
                            {variants.map((v, i) => (
                                <button
                                    key={v.sku || v.label || i}
                                    type="button"
                                    onClick={() => setSelected(i)}
                                    data-testid={`material-variant-${material.id}-${i}`}
                                    className={`px-3 py-1.5 text-[0.7rem] font-medium border rounded-sm transition-colors duration-200 ${
                                        i === selected
                                            ? "bg-wine text-white border-wine"
                                            : "bg-white text-slate-600 border-taupe/60 hover:border-wine/50 hover:text-wine"
                                    }`}
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Price + COA */}
                <div className="mt-4 pt-4 border-t border-taupe/50 flex items-center justify-between gap-3">
                    <span className="font-serif text-lg text-wine" data-testid={`material-price-${material.id}`}>
                        {price || "Price TBA"}
                    </span>
                    {variant?.coa_url ? (
                        <a
                            href={variant.coa_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`material-coa-${material.id}`}
                            className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.18em] font-semibold text-wine hover:text-wine-deep transition-colors duration-300"
                        >
                            <FileText className="h-3.5 w-3.5" /> View COA
                        </a>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.18em] font-semibold text-slate-400">
                            <FileText className="h-3.5 w-3.5" /> COA pending
                        </span>
                    )}
                </div>
                {variant && (variant.sku || variant.lot_batch) && (
                    <p className="mt-2 text-[0.6rem] text-slate-400" data-testid={`material-sku-${material.id}`}>
                        {[variant.sku && `SKU ${variant.sku}`, variant.lot_batch && `Lot ${variant.lot_batch}`]
                            .filter(Boolean)
                            .join(" · ")}
                    </p>
                )}

                {/* Actions */}
                <div className="mt-auto pt-4">
                    <button
                        type="button"
                        onClick={handleAdd}
                        disabled={!variants.length || inCart}
                        data-testid={`material-add-${material.id}`}
                        className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 uppercase tracking-[0.2em] text-[0.65rem] font-semibold transition-colors duration-300 ${
                            !variants.length
                                ? "bg-surface2 text-slate-400 border border-stone-200 cursor-not-allowed"
                                : inCart
                                  ? "bg-surface2 text-slate-400 border border-stone-200 cursor-default"
                                  : "bg-wine text-white hover:bg-gold-bright"
                        }`}
                    >
                        <ShoppingBag className="h-3.5 w-3.5" />
                        {!variants.length ? "Catalog Data Pending" : inCart ? "In Cart" : "Add to Cart"}
                    </button>
                    <div className="mt-3 flex items-center justify-between gap-4">
                        <Link
                            to={`/materials/${material.id}`}
                            data-testid={`material-view-${material.id}`}
                            className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-wine hover:text-wine-deep transition-colors duration-300"
                        >
                            View Details <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
                <p className="mt-3 text-[0.6rem] leading-relaxed text-slate-400">{material.research_use_disclaimer}</p>
            </div>
        </article>
    );
}
